import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { API } from "@/App";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Link non valido");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success("Password aggiornata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 flex" data-testid="reset-password-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-stone-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(180,160,120,0.06),transparent_70%)]" />
        <div className="relative z-10 text-center px-12">
          <img src="/logo-uol.png" alt="UOL Paper" className="h-20 w-auto mx-auto mb-6" />
          <p className="text-stone-500 text-lg font-light leading-relaxed max-w-md">
            Reimposta la tua password in sicurezza
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-stone-500 hover:text-white transition-colors mb-12 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="uppercase tracking-[0.15em] text-xs">Accedi</span>
          </button>

          <div className="lg:hidden mb-8">
            <img src="/logo-uol.png" alt="UOL Paper" className="h-12 w-auto" />
          </div>

          {!token ? (
            <div className="space-y-6">
              <div className="flex items-center gap-3 text-amber-400">
                <AlertTriangle className="w-6 h-6" />
                <h2 className="text-2xl font-light text-white">Link non valido</h2>
              </div>
              <p className="text-stone-500 text-sm">Il link di reset è mancante o non è valido. Richiedine uno nuovo.</p>
              <Button
                onClick={() => navigate("/forgot-password")}
                className="w-full rounded-none bg-white text-stone-950 hover:bg-stone-200 h-12 text-sm uppercase tracking-[0.15em]"
              >
                Richiedi nuovo link
              </Button>
            </div>
          ) : done ? (
            <div className="space-y-6" data-testid="reset-done-panel">
              <div className="flex items-center gap-3 text-emerald-400">
                <CheckCircle2 className="w-6 h-6" />
                <h2 className="text-2xl font-light text-white">Password aggiornata</h2>
              </div>
              <p className="text-stone-500 text-sm">Ora puoi accedere con la nuova password.</p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full rounded-none bg-white text-stone-950 hover:bg-stone-200 h-12 text-sm uppercase tracking-[0.15em]"
                data-testid="reset-goto-login-btn"
              >
                Vai al login
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-light text-white mb-2">Nuova password</h2>
              <p className="text-stone-500 text-sm mb-8">Scegli una password sicura (minimo 6 caratteri).</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Nuova password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                    <Input
                      data-testid="reset-password-input"
                      type={showPw ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="rounded-none bg-transparent border-white/10 text-white pl-10 pr-11 h-11 focus:border-amber-300/40"
                      placeholder="Min. 6 caratteri"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(v => !v)}
                      aria-label={showPw ? "Nascondi password" : "Mostra password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-white transition-colors"
                      data-testid="reset-toggle-visibility"
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Conferma password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                    <Input
                      data-testid="reset-confirm-input"
                      type={showPw ? "text" : "password"}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      className="rounded-none bg-transparent border-white/10 text-white pl-10 h-11 focus:border-amber-300/40"
                      placeholder="Ripeti la password"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <Button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-none bg-white text-stone-950 hover:bg-stone-200 h-12 text-sm uppercase tracking-[0.15em]"
                >
                  {loading ? "Aggiornamento..." : "Reimposta password"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
